import { incentivesBaseRoute, performanceBaseRoute, Routes } from '@routes';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { FunctionComponent } from 'react';
import { gidRegex } from 'src/constant';
import styled from 'styled-components';
import Breadcrumbs, { BreadcrumbItem } from '../breadcrumbs';
import Menu, { MenuItem, Submenu } from '../menu';
import NavBar from '../navbar';
import { StyledElement } from '../types';

const Nav = styled(NavBar)`
  background-color: ${({ theme }) => theme.colors.backgroundSecondary.hex};
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider.hex};
`;

const MenuStyled = styled(Menu)`
  border-right: 1px solid ${({ theme }) => theme.colors.divider.hex};
`;

const NavbarBreadcrumbs = styled(Breadcrumbs)`
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavigationBar: FunctionComponent<StyledElement> = ({ className }) => {
  const router = useRouter();
  const routes = router.asPath
    .split('?')[0]
    .split('/')
    .filter((path) => path !== '');

  const breadcrumbItems: BreadcrumbItem[] = routes.map((route, index) => {
    let content: string = route.split('-').join(' ');
    let url = '/' + routes.slice(0, index + 1).join('/');
    let nameMapping: Record<string, string> = {};
    const isIncentivePath = router.asPath.startsWith(incentivesBaseRoute);
    const isPerformancePath = router.asPath.startsWith(performanceBaseRoute);

    if (isIncentivePath) {
      nameMapping = {
        charities: 'featured charities',
        offer: 'offer settings',
      };
    } else if (isPerformancePath) {
      nameMapping = {
        orders: 'qualifying orders',
      };
    }

    content = nameMapping[content] ?? content;

    if (gidRegex.test(route)) {
      content = route;
      if (isIncentivePath) {
        url = Routes.incentives.overview.replace(':gid', route);
      }
    }
    return {
      content,
      url,
    };
  });

  return (
    <Nav className={className}>
      <MenuStyled
        title="Givz"
        items={[
          {
            content: (
              <MenuItem>
                <Link href={Routes.overview}>
                  <a>Overview</a>
                </Link>
              </MenuItem>
            ),
          },
          {
            content: (
              <MenuItem>
                <Link href={Routes.incentives.list}>
                  <a>Incentives</a>
                </Link>
              </MenuItem>
            ),
          },
          {
            content: (
              <Submenu
                title="Performance"
                items={[
                  {
                    content: (
                      <MenuItem>
                        <Link href={Routes.performance.overview}>
                          <a>Overview</a>
                        </Link>
                      </MenuItem>
                    ),
                  },
                  {
                    content: (
                      <MenuItem>
                        <Link href={Routes.performance.donations}>
                          <a>Donations</a>
                        </Link>
                      </MenuItem>
                    ),
                  },
                  {
                    content: (
                      <MenuItem>
                        <Link href={Routes.performance.orders}>
                          <a>Qualifying Orders</a>
                        </Link>
                      </MenuItem>
                    ),
                  },
                  {
                    content: (
                      <MenuItem>
                        <Link href={Routes.performance.charities}>
                          <a>Charities</a>
                        </Link>
                      </MenuItem>
                    ),
                  },
                ]}
              />
            ),
          },
          {
            content: (
              <Submenu
                title="Account"
                items={[
                  {
                    content: (
                      <MenuItem>
                        <Link href={Routes.account.subscription}>
                          <a>Subscription</a>
                        </Link>
                      </MenuItem>
                    ),
                  },
                  {
                    content: (
                      <MenuItem>
                        <Link href={Routes.account.invoices}>
                          <a>Invoices</a>
                        </Link>
                      </MenuItem>
                    ),
                  },
                  {
                    content: (
                      <MenuItem>
                        <Link href={Routes.account.profile}>
                          <a>Profile</a>
                        </Link>
                      </MenuItem>
                    ),
                  },
                  {
                    content: (
                      <MenuItem>
                        <Link href={Routes.account.integrations}>
                          <a>Integrations</a>
                        </Link>
                      </MenuItem>
                    ),
                  },
                ]}
              />
            ),
          },
        ]}
      />
      <NavbarBreadcrumbs items={breadcrumbItems} />
    </Nav>
  );
};

export default NavigationBar;
